/**
 * API client functions for purchase order operations (Phase 1a).
 */

import { API_BASE_URL } from '../config/api';
import type {
  PurchaseOrder,
  PurchaseOrderLineItem,
  CreatePurchaseOrderPayload,
  ReceivePOLineItemPayload,
  ReceivePOLineItemResult,
} from '../types/inventory';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

export async function listPurchaseOrders(
  practiceId: string,
  opts?: { status?: string; search?: string }
): Promise<PurchaseOrder[]> {
  const params = new URLSearchParams();
  if (opts?.status) params.append('status', opts.status);
  if (opts?.search) params.append('search', opts.search);

  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/purchase-orders?${params.toString()}`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch purchase orders' }));
    throw new Error(error.detail || 'Failed to fetch purchase orders');
  }
  return response.json();
}

export async function getPurchaseOrder(practiceId: string, poId: string): Promise<PurchaseOrder> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/purchase-orders/${poId}`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch purchase order' }));
    throw new Error(error.detail || 'Failed to fetch purchase order');
  }
  return response.json();
}

export async function createPurchaseOrder(
  practiceId: string,
  payload: CreatePurchaseOrderPayload
): Promise<PurchaseOrder> {
  const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/purchase-orders`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create purchase order' }));
    throw new Error(error.detail || 'Failed to create purchase order');
  }
  return response.json();
}

export async function submitPurchaseOrder(practiceId: string, poId: string): Promise<PurchaseOrder> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/purchase-orders/${poId}/submit`,
    { method: 'POST', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to submit purchase order' }));
    throw new Error(error.detail || 'Failed to submit purchase order');
  }
  return response.json();
}

export async function cancelPurchaseOrder(practiceId: string, poId: string): Promise<PurchaseOrder> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/purchase-orders/${poId}/cancel`,
    { method: 'POST', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to cancel purchase order' }));
    throw new Error(error.detail || 'Failed to cancel purchase order');
  }
  return response.json();
}

export async function createPurchaseOrderLineItem(
  practiceId: string,
  poId: string,
  payload: {
    catalog_item_id?: string | null;
    catalog_type?: string | null;
    description: string;
    line_number: number;
    quantity_ordered: number;
    unit_price: string;
    notes?: string | null;
  }
): Promise<PurchaseOrderLineItem> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/purchase-orders/${poId}/line-items`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create line item' }));
    throw new Error(error.detail || 'Failed to create line item');
  }
  return response.json();
}

export async function receivePurchaseOrderLineItem(
  practiceId: string,
  poId: string,
  payload: ReceivePOLineItemPayload
): Promise<ReceivePOLineItemResult> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/purchase-orders/${poId}/receive-line-item`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to receive line item' }));
    throw new Error(error.detail || 'Failed to receive line item');
  }
  return response.json();
}


